import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Loader2, Shield, Package, Users, ShoppingBag, Image as ImageIcon, Tag, Settings2, MapPin, Menu, X, Star, Percent, Ticket, AlertTriangle, ShieldAlert } from 'lucide-react';
import DashboardSection from './DashboardSection';
import { useSiteBrand } from '../../components/BrandLogo';
import ProductsSection from './ProductsSection';
import CategoriesSection from './CategoriesSection';
import BannersSection from './BannersSection';
import OrdersSection from './OrdersSection';
import UsersSection from './UsersSection';
import SettingsSection from './SettingsSection';
import CoverageSection from './CoverageSection';
import ReviewsSection from './ReviewsSection';
import PromotionsSection from './PromotionsSection';
import CouponsSection from './CouponsSection';
import IPAlertsSection from './IPAlertsSection';
import IPDuplicatesSection from './IPDuplicatesSection';

const SECTIONS = [
  { id: 'dashboard', label: 'Dashboard', icon: Shield },
  { id: 'products', label: 'Productos', icon: Package },
  { id: 'categories', label: 'Categorías', icon: Tag },
  { id: 'banners', label: 'Banners', icon: ImageIcon },
  { id: 'promotions', label: 'Promociones', icon: Percent },
  { id: 'coupons', label: 'Cupones', icon: Ticket },
  { id: 'ip-alerts', label: 'Alertas IP', icon: AlertTriangle },
  { id: 'ip-duplicates', label: 'IPs Duplicadas', icon: ShieldAlert },
  { id: 'coverage', label: 'Cobertura', icon: MapPin },
  { id: 'orders', label: 'Pedidos', icon: ShoppingBag },
  { id: 'reviews', label: 'Reseñas', icon: Star },
  { id: 'users', label: 'Usuarios', icon: Users },
  { id: 'settings', label: 'Ajustes', icon: Settings2 },
];

const AdminPage = () => {
  const { user, loading: authLoading } = useAuth();
  const [params, setParams] = useSearchParams();
  const active = params.get('s') || 'dashboard';
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const brand = useSiteBrand();

  const setActive = (id) => {
    setParams({ s: id });
    setSidebarOpen(false);
  };

  if (authLoading) return (
    <div className="max-w-6xl mx-auto px-4 mt-20 text-white/70">
      <Loader2 className="w-6 h-6 animate-spin inline" /> Cargando...
    </div>
  );

  if (!user || !user.is_admin) return (
    <div className="max-w-2xl mx-auto px-4 mt-20 pb-32 text-center">
      <div className="w-20 h-20 mx-auto rounded-full bg-rose-500/15 text-rose-400 flex items-center justify-center">
        <Shield className="w-8 h-8" />
      </div>
      <h1 className="mt-6 text-3xl font-black text-white">Acceso denegado</h1>
      <p className="mt-2 text-white/60">Solo administradores pueden ver esta sección.</p>
    </div>
  );

  return (
    <div className="bg-neutral-950 min-h-[calc(100vh-3.5rem)] lg:min-h-[calc(100vh-4rem)] -mt-px">
      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-14 sm:top-16 z-20 bg-black/90 backdrop-blur border-b border-white/10 px-4 py-3 flex items-center gap-3">
        <button onClick={() => setSidebarOpen(true)} className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white">
          <Menu className="w-4 h-4" />
        </button>
        <span className="text-white font-bold text-sm">
          {SECTIONS.find((s) => s.id === active)?.label}
        </span>
      </div>

      <div className="lg:grid lg:grid-cols-[260px_1fr]">
        {/* Sidebar */}
        <aside
          className={`fixed lg:sticky top-0 left-0 h-screen lg:top-14 sm:lg:top-16 lg:h-[calc(100vh-3.5rem)] sm:lg:h-[calc(100vh-4rem)] z-40 w-[85%] max-w-xs lg:max-w-none lg:w-full bg-neutral-950 border-r border-white/[0.06] transform lg:transform-none transition-transform duration-300 ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          }`}
        >
          <div className="p-4 flex items-center justify-between border-b border-white/[0.06]">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg shadow-orange-500/25">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-white font-black text-sm">Admin</p>
                <p className="text-white/45 text-[10px] tracking-widest uppercase truncate">{brand.site_name || 'NubesMTY'}</p>
              </div>
            </div>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-white">
              <X className="w-4 h-4" />
            </button>
          </div>

          <nav className="p-3 space-y-1 overflow-y-auto max-h-[calc(100vh-5rem)] lg:max-h-[calc(100vh-8rem)]">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              const isActive = active === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => setActive(s.id)}
                  className={`w-full h-11 rounded-xl px-3 flex items-center gap-3 text-sm font-semibold transition-all ${
                    isActive
                      ? 'bg-orange-500 text-black shadow-md shadow-orange-500/25'
                      : 'text-white/70 hover:text-white hover:bg-white/[0.04]'
                  }`}
                  data-testid={`admin-nav-${s.id}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="flex-1 text-left">{s.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {sidebarOpen && (
          <div onClick={() => setSidebarOpen(false)} className="lg:hidden fixed inset-0 bg-black/60 z-30 backdrop-blur-sm" />
        )}

        {/* Main */}
        <main className="px-4 md:px-6 lg:px-8 py-6 lg:py-8 max-w-[1400px] mx-auto w-full">
          {active === 'dashboard' && <DashboardSection user={user} />}
          {active === 'products' && <ProductsSection />}
          {active === 'categories' && <CategoriesSection />}
          {active === 'banners' && <BannersSection />}
          {active === 'promotions' && <PromotionsSection />}
        {active === 'coupons' && <CouponsSection />}
        {active === 'ip-alerts' && <IPAlertsSection />}
          {active === 'ip-duplicates' && <IPDuplicatesSection />}
          {active === 'coverage' && <CoverageSection />}
          {active === 'orders' && <OrdersSection />}
          {active === 'reviews' && <ReviewsSection />}
          {active === 'users' && <UsersSection />}
          {active === 'settings' && <SettingsSection />}
        </main>
      </div>
    </div>
  );
};

export default AdminPage;
